import { useState } from 'react';

interface ConsultationFormData {
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  type_consultation: string;
  motif: string;
  diagnostique: string;
  traitement: string;
  medicaments: string;
  notes_medecin: string;
  medecin_id: string;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface ConsultationFormProps {
  medecins: Medecin[];
  onSubmit: (data: ConsultationFormData) => void;
  onCancel: () => void;
  loading: boolean;
  initialData?: Partial<ConsultationFormData>;
}

const TYPES_CONSULTATION = [
  'Consultation générale',
  'Consultation spécialisée',
  'Urgence',
  'Suivi',
  'Contrôle',
  'Visite à domicile',
  'Télémédecine',
];

export default function ConsultationForm({ medecins, onSubmit, onCancel, loading, initialData }: ConsultationFormProps) {
  const [form, setForm] = useState<ConsultationFormData>({
    date_rdv: initialData?.date_rdv || '',
    heure_debut: initialData?.heure_debut || '09:00',
    heure_fin: initialData?.heure_fin || '09:30',
    type_consultation: initialData?.type_consultation || '',
    motif: initialData?.motif || '',
    diagnostique: initialData?.diagnostique || '',
    traitement: initialData?.traitement || '',
    medicaments: initialData?.medicaments || '',
    notes_medecin: initialData?.notes_medecin || '',
    medecin_id: initialData?.medecin_id || '',
  });

  const update = (field: keyof ConsultationFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date_rdv || !form.medecin_id) {
      alert('Veuillez remplir la date et sélectionner un médecin.');
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            required
            value={form.date_rdv}
            onChange={(e) => update('date_rdv', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Heure début</label>
          <input
            type="time"
            value={form.heure_debut}
            onChange={(e) => update('heure_debut', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Heure fin</label>
          <input
            type="time"
            value={form.heure_fin}
            onChange={(e) => update('heure_fin', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Médecin *</label>
          <select
            required
            value={form.medecin_id}
            onChange={(e) => update('medecin_id', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
          >
            <option value="">Sélectionner un médecin...</option>
            {medecins.map((m) => (
              <option key={m.id} value={m.id}>
                Dr. {m.nom} {m.prenom} - {m.specialite}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Type de consultation</label>
          <select
            value={form.type_consultation}
            onChange={(e) => update('type_consultation', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
          >
            <option value="">Sélectionner...</option>
            {TYPES_CONSULTATION.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Motif de consultation</label>
        <textarea
          value={form.motif}
          onChange={(e) => update('motif', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Motif de la consultation..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Diagnostic</label>
          <textarea
            value={form.diagnostique}
            onChange={(e) => update('diagnostique', e.target.value)}
            rows={2}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
            placeholder="Diagnostic posé..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Traitement prescrit</label>
          <textarea
            value={form.traitement}
            onChange={(e) => update('traitement', e.target.value)}
            rows={2}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
            placeholder="Traitement prescrit..."
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Médicaments</label>
        <textarea
          value={form.medicaments}
          onChange={(e) => update('medicaments', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Médicaments prescrits..."
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Notes du médecin</label>
        <textarea
          value={form.notes_medecin}
          onChange={(e) => update('notes_medecin', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Notes supplémentaires..."
        />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2.5 bg-clinic-blue-dark text-white rounded-xl hover:bg-clinic-blue transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Enregistrement...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Enregistrer la consultation
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export type { ConsultationFormData };